"use client";

import { Text, SectionCard, CardTitle, CardDescription, Badge, Section, Alert } from "@/src/components";

export default function InstallationPage() {
  return (
    <>
      {/* Header */}
      <Section>
        <div className="space-y-4">
          <Text as="h1" className="text-h2 font-bold">
            Installation
          </Text>
          <Text className="text-lg text-muted-foreground max-w-2xl">
            Get started with Dream Light by installing components into your project.
          </Text>
        </div>
      </Section>

      {/* Prerequisites */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Prerequisites</CardTitle>
              <CardDescription>Make sure you have these installed</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 border rounded-lg text-center">
              <Text className="font-semibold">Node.js</Text>
              <Badge variant="secondary" className="mt-2">18.x+</Badge>
            </div>
            <div className="p-4 border rounded-lg text-center">
              <Text className="font-semibold">React</Text>
              <Badge variant="secondary" className="mt-2">18.x+</Badge>
            </div>
            <div className="p-4 border rounded-lg text-center">
              <Text className="font-semibold">Tailwind CSS</Text>
              <Badge variant="secondary" className="mt-2">4.x</Badge>
            </div>
          </div>
        </SectionCard>
      </Section>

      {/* Quick Start */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Quick Start with CLI</CardTitle>
              <CardDescription>The fastest way to add components</CardDescription>
            </>
          }
          contentClassName="p-6 space-y-4"
        >
          <Text className="text-sm">
            Use the shadcn CLI to add components directly to your project:
          </Text>

          <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm space-y-2">
          <code className="block text-muted-foreground"># List all components</code>
          <code className="block">npx shadcn@latest list @bluecopa-ui</code>
            <code className="block text-muted-foreground"># Add a single component</code>
            <code className="block">npx shadcn@latest add @bluecopa-ui/button</code>
            <code className="block mt-4 text-muted-foreground"># Add multiple components</code>
            <code className="block">npx shadcn@latest add @bluecopa-ui/button @bluecopa-ui/input @bluecopa-ui/select</code>
          </div>
        </SectionCard>
      </Section>

      {/* Manual Installation */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Manual Installation</CardTitle>
              <CardDescription>Step-by-step setup for full control</CardDescription>
            </>
          }
          contentClassName="p-6 space-y-6"
        >
          {/* Step 1 */}
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Badge>1</Badge>
              <Text className="font-semibold">Install dependencies</Text>
            </div>
            <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm">
              <code>pnpm add clsx tailwind-merge class-variance-authority</code>
            </div>
          </div>

          {/* Step 2 */}
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Badge>2</Badge>
              <Text className="font-semibold">Add utility function</Text>
            </div>
            <Text className="text-sm text-muted-foreground">
              Create <code className="bg-muted px-1 rounded">lib/utils.ts</code>:
            </Text>
            <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm space-y-1">
              <code className="block">{"import { clsx, type ClassValue } from \"clsx\""}</code>
              <code className="block">{"import { twMerge } from \"tailwind-merge\""}</code>
              <code className="block mt-2">{"export function cn(...inputs: ClassValue[]) {"}</code>
              <code className="block pl-4">{"return twMerge(clsx(inputs))"}</code>
              <code className="block">{"}"}</code>
            </div>
          </div>

          {/* Step 3 */}
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Badge>3</Badge>
              <Text className="font-semibold">Add CSS variables</Text>
            </div>
            <Text className="text-sm text-muted-foreground">
              Add these to your <code className="bg-muted px-1 rounded">globals.css</code>:
            </Text>
            <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm max-h-64 overflow-auto space-y-1">
              <code className="block">{"@import \"tailwindcss\";"}</code>
              <code className="block mt-2">{":root {"}</code>
              <code className="block pl-4">{"/* Colors */"}</code>
              <code className="block pl-4">{"--primary: #3548ff;"}</code>
              <code className="block pl-4">{"--primary-foreground: oklch(100% 0 0);"}</code>
              <code className="block pl-4">{"--background: oklch(1 0 0);"}</code>
              <code className="block pl-4">{"--foreground: oklch(0.145 0 0);"}</code>
              <code className="block pl-4">{"/* ... more tokens */"}</code>
              <code className="block mt-2 pl-4">{"/* Spacing */"}</code>
              <code className="block pl-4">{"--spacing-sm: 0.5rem;"}</code>
              <code className="block pl-4">{"--spacing-md: 1rem;"}</code>
              <code className="block pl-4">{"--spacing-lg: 1.5rem;"}</code>
              <code className="block pl-4">{"--spacing-xl: 2rem;"}</code>
              <code className="block">{"}"}</code>
            </div>
          </div>

          {/* Step 4 */}
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Badge>4</Badge>
              <Text className="font-semibold">Copy style drivers</Text>
            </div>
            <Text className="text-sm text-muted-foreground">
              Copy <code className="bg-muted px-1 rounded">lib/componentStyle.ts</code> to your project.
              This contains all the style drivers used by components.
            </Text>
          </div>

          {/* Step 5 */}
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Badge>5</Badge>
              <Text className="font-semibold">Copy components</Text>
            </div>
            <Text className="text-sm text-muted-foreground">
              Copy the component files from <code className="bg-muted px-1 rounded">registry/new-york/ui/</code>
              to your project's component directory.
            </Text>
          </div>
        </SectionCard>
      </Section>

      {/* Project Structure */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Recommended Project Structure</CardTitle>
              <CardDescription>Organize your files for maintainability</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm space-y-1">
            <code className="block">{"your-project/"}</code>
            <code className="block pl-4">{"├── app/"}</code>
            <code className="block pl-8">{"├── globals.css          # CSS variables"}</code>
            <code className="block pl-8">{"└── layout.tsx           # Root layout"}</code>
            <code className="block pl-4">{"├── components/"}</code>
            <code className="block pl-8">{"└── ui/                   # UI components"}</code>
            <code className="block pl-12">{"├── button.tsx"}</code>
            <code className="block pl-12">{"├── input.tsx"}</code>
            <code className="block pl-12">{"└── ..."}</code>
            <code className="block pl-4">{"├── lib/"}</code>
            <code className="block pl-8">{"├── componentStyle.ts     # Style drivers"}</code>
            <code className="block pl-8">{"└── utils.ts              # Utility functions"}</code>
            <code className="block pl-4">{"└── public/"}</code>
            <code className="block pl-8">{"└── fonts/                # Custom fonts"}</code>
          </div>
        </SectionCard>
      </Section>

      {/* Font Setup */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Font Setup</CardTitle>
              <CardDescription>Configure the Satoshi font family</CardDescription>
            </>
          }
          contentClassName="p-6 space-y-4"
        >
          <Text className="text-sm text-muted-foreground">
            Download Satoshi font files and add them to <code className="bg-muted px-1 rounded">public/fonts/</code>:
          </Text>
          <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm space-y-1">
            <code className="block">{"public/fonts/"}</code>
            <code className="block pl-4">{"├── Satoshi-Regular.woff"}</code>
            <code className="block pl-4">{"├── Satoshi-Medium.woff"}</code>
            <code className="block pl-4">{"├── Satoshi-Bold.woff"}</code>
            <code className="block pl-4">{"└── Satoshi-Black.woff"}</code>
          </div>
          <Text className="text-sm text-muted-foreground">
            Then add the @font-face declarations to your CSS (already included in globals.css template).
          </Text>
        </SectionCard>
      </Section>

      {/* Radix UI */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Radix UI Dependencies</CardTitle>
              <CardDescription>Components that require Radix primitives</CardDescription>
            </>
          }
          contentClassName="p-6 space-y-4"
        >
          <Text className="text-sm text-muted-foreground">
            Many components are built on Radix UI primitives. Install them as needed:
          </Text>
          <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm space-y-1">
            <code className="block text-muted-foreground"># Commonly used primitives</code>
            <code className="block">{"pnpm add @radix-ui/react-dialog \\"}</code>
            <code className="block pl-8">{"@radix-ui/react-dropdown-menu \\"}</code>
            <code className="block pl-8">{"@radix-ui/react-popover \\"}</code>
            <code className="block pl-8">{"@radix-ui/react-select \\"}</code>
            <code className="block pl-8">{"@radix-ui/react-tabs \\"}</code>
            <code className="block pl-8">{"@radix-ui/react-tooltip \\"}</code>
            <code className="block pl-8">{"@radix-ui/react-switch \\"}</code>
            <code className="block pl-8">{"@radix-ui/react-accordion"}</code>
          </div>
        </SectionCard>
      </Section>

      {/* TypeScript Config */}
      <Section>
        <SectionCard
          header={<CardTitle>TypeScript Path Alias</CardTitle>}
          contentClassName="p-6 space-y-4"
        >
          <Text className="text-sm text-muted-foreground">
            Configure the @ path alias in <code className="bg-muted px-1 rounded">tsconfig.json</code>:
          </Text>
          <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm space-y-1">
            <code className="block">{"{"}</code>
            <code className="block pl-4">{"\"compilerOptions\": {"}</code>
            <code className="block pl-8">{"\"paths\": {"}</code>
            <code className="block pl-12">{"\"@/*\": [\"./*\"]"}</code>
            <code className="block pl-8">{"}"}</code>
            <code className="block pl-4">{"}"}</code>
            <code className="block">{"}"}</code>
          </div>
        </SectionCard>
      </Section>

      {/* Next Steps */}
      <Section>
        <Alert
          variant="default"
          title="You're ready to go!"
          description="Start using components in your project. Check out the component documentation for usage examples and best practices."
        />
      </Section>
    </>
  );
}
